import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';

// ============================================================
// Tagging Coverage — service recency per account.
// "Which accounts haven't had tagging work in X days?" — including
// never-serviced. Built for the catch-up era: once the queue is
// under control, this is where proactive tagging work gets found.
// ============================================================

const THRESHOLDS = [30, 60, 90, 180, 365];

const chip = (bg, color) => ({
  fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
  background: bg, color, textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap',
});

const REGION_THEMES = {
  'Fort Worth': { bg: '#fef3c7', fg: '#92400e' },
  'Dallas': { bg: '#dbeafe', fg: '#1e40af' },
  'Denton': { bg: '#d1fae5', fg: '#065f46' },
};
const regionTheme = (r) => REGION_THEMES[r] || { bg: '#f1f5f9', fg: '#334155' };

function daysSince(dateStr, today) {
  if (!dateStr) return null;
  const d = new Date(`${String(dateStr).slice(0, 10)}T12:00:00Z`);
  const t = new Date(`${today}T12:00:00Z`);
  return Math.max(0, Math.round((t - d) / 86400000));
}

function Stat({ label, value, accent }) {
  return (
    <div className="card" style={{ padding: '14px 18px', minWidth: 150, flex: 1 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: accent || 'var(--text-primary)', letterSpacing: '-0.01em' }}>{value}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function TaggingCoveragePage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [days, setDays] = useState(90);
  const [region, setRegion] = useState('');
  const [search, setSearch] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(true);

  useEffect(() => {
    api.getTaggingCoverage().then(setData).catch(e => setError(e.message));
  }, []);

  if (error) return <div className="admin-page"><p className="alert alert-error">{error}</p></div>;
  if (!data) return <div className="admin-page"><p>Loading…</p></div>;

  const enriched = data.accounts.map(a => ({ ...a, days_since: daysSince(a.last_serviced, data.today) }));
  const never = enriched.filter(a => a.days_since === null);
  const overdue = enriched.filter(a => a.days_since === null || a.days_since >= days);

  const q = search.trim().toLowerCase();
  const shown = (overdueOnly ? overdue : enriched)
    .filter(a => !region || a.region === region)
    .filter(a => !q || a.name.toLowerCase().includes(q) || (a.address || '').toLowerCase().includes(q) || (a.city || '').toLowerCase().includes(q));

  return (
    <div className="admin-page" style={{ maxWidth: 1100 }}>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2>Tagging Coverage</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Every active account by last completed tagging work — find who's overdue before they call.
          </p>
        </div>
        <Link to="/sales/requests/tagging_request/schedule" className="btn btn-muted" style={{ fontWeight: 600, fontSize: 13 }}>← Tagging Schedule</Link>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Active accounts" value={enriched.length} />
        <Stat label="Never serviced" value={never.length} accent={never.length > 0 ? '#b45309' : undefined} />
        <Stat label={`No service in ${days}+ days`} value={overdue.length} accent={overdue.length > 0 ? '#b91c1c' : '#059669'} />
        <Stat label="With open requests" value={enriched.filter(a => a.open_jobs > 0).length} />
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ padding: '8px 10px' }} value={days} onChange={e => setDays(Number(e.target.value))}>
          {THRESHOLDS.map(d => <option key={d} value={d}>Not serviced in {d}+ days</option>)}
        </select>
        <select className="form-input" style={{ padding: '8px 10px' }} value={region} onChange={e => setRegion(e.target.value)}>
          <option value="">All regions</option>
          {['Fort Worth', 'Dallas', 'Denton'].map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <input className="form-input" style={{ padding: '8px 10px', flex: 1, minWidth: 180 }}
          placeholder="Search account, address, city…" value={search} onChange={e => setSearch(e.target.value)} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} />
          Overdue only
        </label>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{shown.length} shown</span>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="m-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-subtle, #f6f8fa)', textAlign: 'left' }}>
                {['Account', 'Region', 'Address', 'Last tagging work', 'Completed jobs', 'Open'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
                  Nothing matches — every account in this view has recent tagging work 🎉
                </td></tr>
              )}
              {shown.map(a => {
                const t = regionTheme(a.region);
                return (
                  <tr key={a.id} style={{ borderTop: '1px solid var(--border-subtle, #edf2f7)' }}>
                    <td style={{ padding: '9px 14px', fontWeight: 700 }}>{a.name}</td>
                    <td style={{ padding: '9px 14px' }}><span style={chip(t.bg, t.fg)}>{a.region}</span></td>
                    <td style={{ padding: '9px 14px', color: 'var(--text-muted)', fontSize: 12.5 }}>{a.address}, {a.city}</td>
                    <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                      {a.days_since === null
                        ? <span style={chip('#fee2e2', '#991b1b')}>Never</span>
                        : <>
                          {String(a.last_serviced).slice(0, 10)}
                          <span style={{
                            marginLeft: 8,
                            ...chip(a.days_since >= days ? '#fef3c7' : '#d1fae5', a.days_since >= days ? '#92400e' : '#065f46'),
                          }}>{a.days_since}d ago</span>
                        </>}
                    </td>
                    <td style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 700 }}>{a.completed_jobs}</td>
                    <td style={{ padding: '9px 14px' }}>
                      {a.open_jobs > 0 ? <span style={chip('#e0f2fe', '#075985')}>{a.open_jobs} open</span> : <span style={{ color: 'var(--text-faint)' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
